import { supabase, isSupabaseConfigured } from '../supabase-client.ts';

const EVENT_TO_STAT_FIELD = {
  summary_read: 'summary_read',
  like_button_clicked: 'like_content',
  content_switch_clicked: 'toggle_content',
  image_toggle_clicked: 'illustration_engaged',
  exit_assessment_button: 'assessment_complete',
};

let booksCatalogCache = null;

const EMPTY_STATS = {
  summary_read: 0,
  like_content: 0,
  toggle_content: 0,
  illustration_engaged: 0,
  assessment_complete: 0,
};

const normalizeInteger = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const normalizeKey = (value) => String(value || '')
  .toLowerCase()
  .replace(/[^a-z0-9]/g, '');

const toKebabCase = (value) => String(value || '')
  .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
  .replace(/[_\s]+/g, '-')
  .replace(/-+/g, '-')
  .toLowerCase()
  .trim();

const getBooksCatalog = async () => {
  if (booksCatalogCache) {
    return booksCatalogCache;
  }

  if (!isSupabaseConfigured || !supabase) {
    return [];
  }

  const { data, error } = await supabase
    .from('BOOKS')
    .select('book_id, title');

  if (error || !Array.isArray(data)) {
    return [];
  }

  booksCatalogCache = data;
  return booksCatalogCache;
};

const resolveCanonicalBook = async ({ bookId, bookTitle }) => {
  const catalog = await getBooksCatalog();
  if (!catalog.length) {
    return {
      book_id: bookId || null,
      book_title: bookTitle || null,
    };
  }

  const normalizedId = normalizeKey(bookId);
  const normalizedTitle = normalizeKey(bookTitle);

  const matchedBook = catalog.find((book) =>
    (bookId && book.book_id === bookId) ||
    (bookTitle && book.title === bookTitle) ||
    (normalizedId && normalizeKey(book.book_id) === normalizedId) ||
    (normalizedTitle && normalizeKey(book.title) === normalizedTitle)
  );

  if (!matchedBook) {
    return {
      book_id: bookId || null,
      book_title: bookTitle || null,
    };
  }

  return {
    book_id: matchedBook.book_id,
    book_title: matchedBook.title,
  };
};

const buildBookFilterValues = (bookId, bookTitle) => {
  const values = new Set();

  if (bookId) {
    values.add(bookId);
    values.add(toKebabCase(bookId));
    values.add(bookId.toLowerCase());
    values.add(normalizeKey(bookId));
  }

  return {
    idValues: [...values].filter(Boolean),
    titleValues: [bookTitle].filter(Boolean),
  };
};

export const logEngagementEvent = async (eventName, properties = {}) => {
  if (!isSupabaseConfigured || !supabase || !eventName) return;

  const userId = properties.user_id || localStorage.getItem('auth_id');
  if (!userId || userId === 'anonymous') return;

  const canonicalBook = await resolveCanonicalBook({
    bookId: properties.book_id,
    bookTitle: properties.book_title,
  });

  const payload = {
    user_id: userId,
    event_name: eventName,
    book_id: canonicalBook.book_id,
    book_title: canonicalBook.book_title,
    chapter_num: normalizeInteger(properties.chapter_num),
    stanza_num: normalizeInteger(properties.stanza_number ?? properties.stanza_num),
    content_type: properties.content_type || null,
    metadata: {
      ...properties,
      book_id: canonicalBook.book_id,
      book_title: canonicalBook.book_title,
    },
  };

  try {
    await supabase.from('ENGAGEMENT_EVENTS').insert(payload);
  } catch (error) {
    console.warn('Failed to persist engagement event:', eventName, error);
  }
};

const fetchCountForEvent = async ({ userId, bookId, bookTitle, eventName }) => {
  if (!isSupabaseConfigured || !supabase || !userId) return 0;

  let query = supabase
    .from('ENGAGEMENT_EVENTS')
    .select('id', { head: true, count: 'exact' })
    .eq('user_id', userId)
    .eq('event_name', eventName);

  if (bookId || bookTitle) {
    const { idValues, titleValues } = buildBookFilterValues(bookId, bookTitle);
    const orFilters = [];
    idValues.forEach((idValue) => {
      orFilters.push(`book_id.eq.${idValue}`);
    });
    titleValues.forEach((titleValue) => {
      orFilters.push(`book_title.eq.${titleValue}`);
    });
    query = query.or(orFilters.join(','));
  }

  const { count, error } = await query;
  if (error) {
    console.warn('Failed to fetch engagement count:', eventName, error);
    return 0;
  }

  return count || 0;
};

export const getEngagementStatsByBook = async ({ userId, bookId = '', bookTitle = '' }) => {
  if (!userId) {
    return { ...EMPTY_STATS };
  }

  const eventNames = Object.keys(EVENT_TO_STAT_FIELD);
  const counts = await Promise.all(
    eventNames.map((eventName) =>
      fetchCountForEvent({ userId, bookId, bookTitle, eventName })
    )
  );

  const stats = { ...EMPTY_STATS };
  eventNames.forEach((eventName, index) => {
    const field = EVENT_TO_STAT_FIELD[eventName];
    stats[field] = counts[index] || 0;
  });

  return stats;
};

export const mergeSummaryReadStat = (baseStats, summaryRead) => ({
  ...baseStats,
  summary_read: Math.max(
    Number.isFinite(Number(baseStats?.summary_read)) ? Number(baseStats.summary_read) : 0,
    Number.isFinite(Number(summaryRead)) ? Number(summaryRead) : 0,
  ),
});
